import {
  DetailedHTMLProps,
  forwardRef,
  ReactElement,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import thematize from 'lib/thematize';
import Error from 'components/Base/Input/Error';
import styles from './Textarea.scss';

const theme = thematize(styles);

type Props = DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> & {
  className?: string;
  containerClassName?: string;
  error?: string;
  errorClassName?: string;
  height?: number;
  invalid?: boolean;
  maxRows?: number;
  value?: string;
};

const Textarea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      invalid = false,
      error = '',
      className = '',
      errorClassName = '',
      value,
      maxRows,
      containerClassName = '',
      height,
      ...rest
    },
    ref
  ): ReactElement => {
    const innerRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const adjustHeight = useCallback(() => {
      const textarea = innerRef.current;

      if (!textarea || typeof maxRows === 'undefined') return;

      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight || '20', 10);
      const maxHeight = lineHeight * maxRows;

      // Устанавливаем минимальную высоту стилем
      if (height) {
        textarea.style.minHeight = `${height}px`;
      }

      textarea.style.height = height ? `${height}px` : 'auto'; // сброс
      const { scrollHeight } = textarea;

      const newHeight = Math.min(scrollHeight, maxHeight);

      textarea.style.height = `${newHeight}px`;
    }, [maxRows, height]);

    useEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    return (
      <div className={`${theme('container')} ${containerClassName}`}>
        <textarea
          ref={innerRef}
          className={`${theme('textarea', {
            invalid: !!(invalid || error),
          })} ${className}`}
          value={value}
          {...rest}
        />
        {error && <Error className={errorClassName} error={error} />}
      </div>
    );
  }
);

export default Textarea;
